import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { Logo } from './Logo.js'
import type { IconName } from './Icon.js'
import { Icon } from './Icon.js'

export interface NavItem {
  to: string
  label: string
  icon?: IconName
}

export interface NavSection {
  label?: string
  items: NavItem[]
}

interface SidebarProps {
  /** Either a flat list (legacy) or grouped sections. */
  items?: NavItem[]
  sections?: NavSection[]
  footer?: ReactNode
}

function NavLink({ item }: { item: NavItem }) {
  return (
    <Link
      to={item.to}
      activeProps={{ className: 'is-active' }}
      className="group relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-fg-muted hover:bg-surface-subtle hover:text-fg transition-colors [&.is-active]:bg-[var(--color-primary-tint)] [&.is-active]:text-fg [&.is-active]:font-medium"
    >
      <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-[var(--color-primary)] opacity-0 group-[.is-active]:opacity-100" />
      {item.icon ? (
        <Icon
          name={item.icon}
          size={16}
          className="text-fg-subtle group-[.is-active]:text-[var(--color-primary)]"
        />
      ) : null}
      <span>{item.label}</span>
    </Link>
  )
}

export function Sidebar({ items, sections, footer }: SidebarProps) {
  const groups: NavSection[] = sections ?? (items ? [{ items }] : [])

  return (
    <aside className="hidden md:flex md:flex-col w-60 shrink-0 border-r border-border bg-surface">
      <div className="px-5 py-5 border-b border-border">
        <Logo />
      </div>
      <nav className="flex-1 px-2 py-3 space-y-4 overflow-y-auto">
        {groups.map((group, gi) => (
          <div key={gi} className="space-y-0.5">
            {group.label ? (
              <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-fg-faint">
                {group.label}
              </p>
            ) : null}
            {group.items.map((item) => (
              <NavLink key={item.to} item={item} />
            ))}
          </div>
        ))}
      </nav>
      {footer ? (
        <div className="border-t border-border px-4 py-3">{footer}</div>
      ) : null}
    </aside>
  )
}
